import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { TokenContext } from "../app/App";
import { getSavedSongs, removeSong, saveNewSong, SongCookie } from "../shared/utils/cookies.utils";
import { getTopGenres } from "../shared/utils/songs.utils";
import { AccessTokenDto, Artist, getArtistById, getRecentlyPlayed, getSongById, getSongsByIds, RecentlyPlayedTrack, ResponseStatus, Track } from "../spotify/spotify";
import SongList from "./SongList";
import SongSelector from "./SongSelector";
import TimeRangeSelector from "./TimeRangeSelector";

const getArtistsForTracks = async (tracks: Track[], accessToken: string, noDuplicates?: boolean): Promise<Artist[] | ResponseStatus.UnknownError | ResponseStatus.Unauthorized> => {
    let artists: Artist[] = [];
    let artistHist: Record<string, Artist> = {};
    for (let song of tracks) {
        for (let artist of song.artists) {
            if (artist.id in artistHist) {
                if (!noDuplicates) {
                    artists.push(artistHist[artist.id]);
                }
            } else {
                const newArtist = await getArtistById(accessToken, artist.id);
                if (newArtist === ResponseStatus.Unauthorized || newArtist === ResponseStatus.UnknownError) {
                    return newArtist;
                } else {
                    artists.push(newArtist);
                    artistHist[artist.id] = newArtist;
                }
            }
        }
    }
    return artists;
}

const Tool: React.FC = () => {
    //const [ token, setToken ] = useState<AccessTokenDto | null>(null);
    const { token, setToken } = useContext(TokenContext);
    const [ searchParams, _setSearchParams ] = useSearchParams();
    const [ status, setStatus ] = useState<"loaded" | "loading" | "success" | "error">("loading");
    const [ tracks, setTracks ] = useState<RecentlyPlayedTrack[]>([]);
    const [ artists, setArtists ] = useState<Artist[]>([]);
    const [ chosenSong, setChosenSong ] = useState<Track | null>(null);
    const topGenres = getTopGenres(artists);

    const collectSongs = async (accessToken: string, afterTimestamp: number) => {
        setStatus("loading");
        const recentSongs = await getRecentlyPlayed(accessToken, afterTimestamp, 50);
        if (recentSongs === ResponseStatus.Unauthorized || recentSongs === ResponseStatus.UnknownError) {
            setStatus("error");
            return;
        }
        //console.log(recentSongs);
        const trackInfo = await getSongsByIds(accessToken, recentSongs.map(s => s.id));
        //console.log(trackInfo);
        if (trackInfo === ResponseStatus.Unauthorized || trackInfo === ResponseStatus.UnknownError) {
            setStatus("error");
            return;
        }
        //console.log(trackInfo);
        const trackArtists = await getArtistsForTracks(recentSongs, accessToken);
        if (trackArtists === ResponseStatus.Unauthorized || trackArtists === ResponseStatus.UnknownError) {
            setStatus("error");
            return;
        }
        console.log(trackArtists);
        setTracks(recentSongs);
        setArtists(trackArtists);
        setStatus("loaded");
    }

    const confirmTimeRange = (afterTimestamp: number) => {
        if (!token) {
            setStatus("error");
            return;
        }
        collectSongs(token.accessToken, afterTimestamp);
    }

    const checkSavedSongs = async (accessToken: string) => {
        // check if song already selected today
        const songCookies = getSavedSongs();
        if (songCookies) {
            const todayStr = moment().format('YYYY-MM-DD');
            const savedSong = songCookies.find(c => c.date === todayStr);
            if (savedSong) {
                setStatus("loaded");
                const song = await getSongById(accessToken, savedSong.spotifyId);
                if (song === ResponseStatus.UnknownError || song === ResponseStatus.Unauthorized) {
                    setStatus("error");
                    return;
                }
                setChosenSong(song);
                setStatus("loaded");
            }
        }
    }

    const addSong = (song: Track) => {
        if (chosenSong === null) {
            const todayStr = moment().format('YYYY-MM-DD');
            saveNewSong({date: todayStr, spotifyId: song.id});
            setTracks([]);
            setArtists([]);
            setChosenSong(song);
        }
    }

    const removeChosenSong = (songId: string) => {
        const todayStr = moment().format('YYYY-MM-DD');
        removeSong(songId, todayStr);
        setChosenSong(null);
    }
    
    useEffect(() => {
        const accessToken = searchParams.get("accessToken");
        const refreshToken = searchParams.get("refreshToken");
        const expiresIn = searchParams.get("expiresIn");
        const now = searchParams.get("now");

        if (!accessToken || !refreshToken || !expiresIn || isNaN(parseInt(expiresIn)) || !now || isNaN(parseInt(now))) {
            setStatus("error");
            return;
        }

        setToken({accessToken, refreshToken, expiresIn: parseInt(expiresIn) });
        setStatus("loaded");
        checkSavedSongs(accessToken);
    }, [ searchParams ]);

    return (
        <div className="w-screen column-centered py-10">
            { 
                status === "loading" && 
                <div className="flex flex-col items-center mt-20">
                    <div className="loading-ellipsis"><div></div><div></div><div></div><div></div></div>
                    <p className="text-center">Loading...</p>
                </div>
            }
            { 
                status === "error" && 
                <>
                    <p>An unknown error occurred.</p> 
                    <Link to="/" className="my-4">Home</Link>
                </>
            }
            { 
                status === "loaded" && 
                <div className="column-centered">
                    <div className="row align-center mx-bw-5">
                        <p className="">Today is</p>
                        <p className="font-bold">{moment().format(" dddd, MMMM Do YYYY")}</p>
                    </div>
                    {
                        chosenSong !== null ?
                        <>
                            <p className="my-5">You have already chosen a song today!</p>
                            <Link to="/history" className="mb-5">View Previous Selections</Link>
                            <div className="column-centered">
                                { chosenSong.album.images.length > 0 && <img src={chosenSong.album.images[0].url} alt="img" className="album-art-cover-img" /> }
                                <p className="my-3">{`Chosen song: ${chosenSong.name} by ${chosenSong.artists.map(a => a.name).join(' ')}`}</p>
                                { 
                                    chosenSong.preview_url &&
                                    <audio controls>
                                        <source src={chosenSong.preview_url} type="audio/mp3" />
                                    </audio>
                                }
                            </div>
                            <button onClick={() => removeChosenSong(chosenSong.id)} className="button rounded-lg border px-2 py-1 my-5">Remove Song</button>
                        </> :
                        tracks.length > 0 ?
                            <div className="column-centered">
                                { 
                                    topGenres && 
                                    <div className="column-centered mx-2 my-5">
                                        <p className="text-center">Today's Top Genres:</p> 
                                        <p className="text-center">{topGenres.join(', ')}</p>
                                    </div>
                                }
                                <SongSelector songList={tracks} addSong={addSong} back={() => {setTracks([]);}} />
                            </div> :
                            <>
                                <TimeRangeSelector confirm={confirmTimeRange} />
                            </>
                        
                    }
                </div>
            }
        </div>
    );
}

export default Tool;
